// Inbox — telas 09 (lista Active/Archived) e 10 (thread agente ↔ humano).
// Contrato dos specs: mensagem = comentário de issue no repo do projeto;
// um card = um comentário, uma thread = uma issue. Nada aqui guarda estado
// próprio — tudo é leitura/derivação do GitHub, e responder é comentar.

import { repoAtual,
  gateway, github, litellm, SourceError,
  type GhComment, type GhIssue,
} from "../api";
import {
  empty, errorBox, esc, head, md, pill, since, skeleton, tabs, toast,
} from "../ui";

const CSS = `
.inbox-list { display: flex; flex-direction: column; gap: 10px; margin-top: 14px; }
.inbox-card { display: flex; gap: 14px; align-items: flex-start; padding: 14px 16px; text-decoration: none; color: inherit; }
.inbox-main { flex: 1; min-width: 0; }
.inbox-from { display: flex; gap: 8px; align-items: center; font-weight: 600; }
.inbox-count { font-size: 11px; line-height: 1.6; opacity: .7; border: 1px solid currentColor; border-radius: 999px; padding: 0 6px; }
.inbox-subject { font-weight: 600; margin: 2px 0; }
.inbox-preview { opacity: .65; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.inbox-meta { display: flex; flex-direction: column; gap: 2px; align-items: flex-end; white-space: nowrap; font-size: 12px; opacity: .7; }
.inbox-back { display: inline-block; margin-bottom: 10px; }
.inbox-msgs { display: flex; flex-direction: column; gap: 12px; margin: 14px 0; }
.inbox-msg { max-width: 85%; padding: 12px 16px; }
.inbox-msg[data-side="you"] { align-self: flex-end; }
.inbox-msg-head { display: flex; justify-content: space-between; gap: 12px; font-size: 12px; opacity: .75; margin-bottom: 6px; }
.inbox-banner { border: 1px solid #d97706; color: #d97706; border-radius: 10px; padding: 10px 14px; margin: 10px 0; }
.inbox-send { display: flex; justify-content: flex-end; margin-top: 8px; }
.inbox-subj { opacity: .6; }
.inbox-q { border: 1px solid #d97706; border-radius: 12px; padding: 12px 14px; margin-top: 10px; }
.inbox-q-head { display: flex; align-items: center; gap: 12px; font-weight: 600; }
.inbox-q-head progress { flex: 1; max-width: 140px; }
.inbox-q-opt { display: flex; gap: 10px; align-items: flex-start; padding: 10px 12px; margin-top: 8px; border: 1px solid currentColor; border-radius: 10px; }
.inbox-q-help { display: block; font-size: 12px; opacity: .7; }
.inbox-q-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
`;
if (!document.getElementById("css-inbox")) {
  const st = document.createElement("style");
  st.id = "css-inbox";
  st.textContent = CSS;
  document.head.append(st);
}


/** A API do comentário não traz o número da issue solto — sai da URL. */
const numeroDaIssue = (issue_url: string): string => issue_url.split("/").pop() ?? "";

/** "Aug 14, 9:08 AM" — formato do spec 10. */
const dataHora = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });

/** Convenção do spec 10 ("Senior Dev — 👋 Hi back"): primeira linha curta
 *  seguida de linha em branco é o subject da mensagem, não corpo. Linha que
 *  começa marcador de markdown é conteúdo, não subject. */
const subjectDaPrimeiraLinha = (corpo: string): string =>
  corpo.match(/^([^\n|#>*\-\d][^\n]{0,59})\r?\n\s*\r?\n/)?.[1].trim() ?? "";

/** Pergunta do agente dentro do corpo do comentário (spec 09, f071): uma linha
 *  "Question N of M — Título", o enunciado logo abaixo e as opções em bullets
 *  no formato "Rótulo — ajuda", com "(recommended)" na sugerida. O que vier
 *  antes do cabeçalho continua sendo corpo em markdown.
 *  ponytail: convenção de texto, não schema — se o agente publicar as opções
 *  em JSON no comentário, trocar este parser por JSON.parse. */
interface Pergunta {
  antes: string;
  n: number;
  total: number;
  titulo: string;
  enunciado: string;
  opcoes: Array<{ label: string; ajuda: string; recomendada: boolean }>;
}

const CABECALHO = /^Question\s+(\d+)\s+of\s+(\d+)\s*(?:[—–-]\s*(.*))?$/i;

function pergunta(corpo: string): Pergunta | null {
  const linhas = corpo.split(/\r?\n/);
  const i = linhas.findIndex((l) => CABECALHO.test(l.trim()));
  if (i < 0) return null;
  const cab = linhas[i].trim().match(CABECALHO)!;

  const resto = linhas.slice(i + 1).map((l) => l.trim()).filter(Boolean);
  const opcoes = resto
    .filter((l) => /^[-*]\s+/.test(l))
    .map((l) => {
      const texto = l.replace(/^[-*]\s+/, "");
      const partes = texto.replace(/\s*\(recommended\)/i, "").split(/\s+[—–]\s+/);
      return {
        label: partes[0].trim(),
        ajuda: partes.slice(1).join(" — ").trim(),
        recomendada: /\(recommended\)/i.test(texto),
      };
    })
    .filter((o) => o.label);

  // Cabeçalho sem opções é texto comum: o form só existe se houver escolha.
  if (opcoes.length < 2) return null;

  return {
    antes: linhas.slice(0, i).join("\n"),
    n: Number(cab[1]),
    total: Number(cab[2]),
    titulo: (cab[3] ?? "").trim(),
    enunciado: resto.find((l) => !/^[-*]\s+/.test(l)) ?? "",
    opcoes,
  };
}

/** O form do pixel. `id` também é o `name` dos radios — um grupo por pergunta.
 *  `voltar` é o id da pergunta anterior já carregada na thread; sem ela, o
 *  "Back" não teria para onde ir e fica fora (contrato: sem escrita). */
function formPergunta(q: Pergunta, id: string, voltar: string | null): string {
  const opcoes = q.opcoes
    .map(
      (o) => `
        <label class="inbox-q-opt">
          <input type="radio" name="${id}" value="${esc(o.label)}" required />
          <span>
            <strong>${esc(o.label)}</strong>${o.recomendada ? ` ${pill("Recommended", "wait")}` : ""}
            ${o.ajuda ? `<span class="inbox-q-help">${esc(o.ajuda)}</span>` : ""}
          </span>
        </label>`,
    )
    .join("");

  // Pixel de f071: "Question 2 of 2" com a barra em 50% — o progresso conta as
  // perguntas já respondidas, não a atual.
  return `
    <form class="inbox-q" id="${id}">
      <div class="inbox-q-head">
        <span>Question ${q.n} of ${q.total}${q.titulo ? ` — ${esc(q.titulo)}` : ""}</span>
        <progress value="${q.n - 1}" max="${q.total}"></progress>
      </div>
      ${q.enunciado ? `<p>${esc(q.enunciado)}</p>` : ""}
      ${opcoes}
      <div class="inbox-q-foot">
        ${voltar ? `<button class="btn" type="button" data-back="${voltar}">Back</button>` : ""}
        <button class="btn btn-primary" type="submit">Submit</button>
      </div>
    </form>`;
}

/** Esqueleto, erro tratado e o `next` da origem — idioma das páginas. */
async function load(el: HTMLElement, header: string, body: () => Promise<string>): Promise<void> {
  el.innerHTML = header + skeleton();
  try {
    el.innerHTML = header + (await body());
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML = header + errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}

type IssueFull = GhIssue & {
  body?: string | null;
  user?: { login: string };
  /** Contagem da thread — o payload traz, a interface do kernel não declara. */
  comments?: number;
};

export const inbox = async (el: HTMLElement): Promise<void> => {
  const seg = location.hash.split("/")[2];
  if (seg && seg !== "archived") return thread(el, seg);
  return lista(el, seg === "archived" ? "archived" : "active");
};

// -------------------------------------------------------------------- lista

/** Único estado local da tela: o último comentário já anunciado no toast. */
const VISTO = "inbox-ultimo-visto";

async function lista(el: HTMLElement, aba: "active" | "archived"): Promise<void> {
  const header = head({
    title: "Inboxes",
    sub: "Messages and updates from your agents",
    actions: `<button class="btn btn-primary" id="inbox-new">New Message</button>`,
  });

  let repo = "";

  await load(el, header, async () => {
    repo = await repoAtual();

    const abas = tabs([
      { label: "Active", href: "#/inbox", ativa: aba === "active" },
      { label: "Archived", href: "#/inbox/archived", ativa: aba === "archived" },
    ]);

    // Comentário de issue não tem estado de arquivo (sem dono no contrato):
    // a aba existe, mas é honesta sobre estar vazia por definição.
    if (aba === "archived") {
      return abas + empty(
        "Nada arquivado",
        "Comentário de issue não tem flag de arquivo — a pergunta sobre o dono desse estado está aberta na inbox do projeto. Active mostra a lista toda.",
      );
    }

    const [comentarios, issuesRaw, eu, agentes] = await Promise.all([
      github.comments(repo),
      github.issues(repo),
      github.me(),
      litellm.agents().catch(() => []),
    ]);
    const issues = issuesRaw as IssueFull[];
    const porNumero = new Map(issues.map((i) => [String(i.number), i]));

    const opcoes = (agentes.length ? agentes.map((a) => a.agent_name) : ["executor"])
      .map((nome) => `<option value="${esc(nome)}">${esc(nome)}</option>`)
      .join("");

    const compor = `
      <div class="card" id="inbox-compose" hidden style="margin-top:14px">
        <div class="card-head"><span class="card-title">New Message</span></div>
        <div class="card-body">
          <form id="inbox-compose-form">
            <div class="field">
              <label class="label" for="inbox-to">Para (agente)</label>
              <select class="input" id="inbox-to" name="agent">${opcoes}</select>
            </div>
            <div class="field">
              <label class="label" for="inbox-subj">Assunto</label>
              <input class="input" id="inbox-subj" name="title" placeholder="Vira o título da issue" />
            </div>
            <div class="field">
              <label class="label" for="inbox-body">Mensagem</label>
              <textarea class="textarea" id="inbox-body" name="body" rows="4"></textarea>
              <span class="hint">Abre uma issue em ${esc(repo)} com o label do agente — a thread nasce dela.</span>
            </div>
            <button class="btn btn-primary" type="submit">Send</button>
          </form>
        </div>
      </div>`;

    if (!comentarios.length) {
      return abas + compor + empty(
        "Nada esperando você",
        `Quando um agente precisar de você, ele comenta na issue em ${repo} e a mensagem aparece aqui.`,
      );
    }

    // Awaiting reply: o comentário mais recente da issue não é seu. A lista
    // vem em ordem decrescente, então a primeira ocorrência é a mais nova.
    const ultimoDaIssue = new Map<string, number>();
    for (const c of comentarios) {
      const n = numeroDaIssue(c.issue_url);
      if (!ultimoDaIssue.has(n)) ultimoDaIssue.set(n, c.id);
    }

    // A listagem repo-wide traz só as 100 primeiras issues, e comentário de PR
    // nem aparece nela — o título que faltar vem pelo endpoint do contrato.
    await Promise.all(
      Array.from(new Set(comentarios.map((c) => numeroDaIssue(c.issue_url))))
        .filter((n) => n && !porNumero.has(n))
        .map((n) =>
          gateway<IssueFull>(`github/repos/${repo}/issues/${n}`)
            .then((i) => void porNumero.set(n, i))
            .catch(() => {}),
        ),
    );

    // Toast do item mais recente (contrato 09: derivado da mesma listagem), só
    // quando ele muda. O "×" do pixel mora no #toast, em ui.ts — fora daqui.
    const novo = comentarios[0];
    const titulo = porNumero.get(numeroDaIssue(novo.issue_url))?.title;
    if (titulo && localStorage.getItem(VISTO) !== String(novo.id)) {
      localStorage.setItem(VISTO, String(novo.id));
      toast(`Ready for review: "${titulo}"`);
    }

    const nomeRepo = repo.split("/")[1] ?? repo;
    const cards = comentarios
      .map((c) => {
        const n = numeroDaIssue(c.issue_url);
        const issue = porNumero.get(n);
        const espera = ultimoDaIssue.get(n) === c.id && c.user.login !== eu.login;
        const contagem = issue?.comments ?? 0;
        const corpo = c.body.replace(/\s+/g, " ").trim();
        return `
        <a class="card inbox-card" href="#/inbox/${esc(n)}">
          <div class="inbox-main">
            <div class="inbox-from">
              <span>${esc(c.user.login)}</span>
              ${contagem > 1 ? `<span class="inbox-count mono">${contagem}</span>` : ""}
              ${espera ? pill("Awaiting reply", "wait") : ""}
            </div>
            <div class="inbox-subject">${esc(issue?.title ?? `#${n}`)}</div>
            <div class="inbox-preview">${esc(corpo.slice(0, 140))}${corpo.length > 140 ? "…" : ""}</div>
          </div>
          <div class="inbox-meta mono">
            <span>${esc(since(c.created_at))}</span>
            <span>${esc(nomeRepo)} Inbox</span>
          </div>
        </a>`;
      })
      .join("");

    return abas + compor + `<div class="inbox-list">${cards}</div>`;
  });

  // Depois do render — os elementos só existem se o corpo carregou.
  el.querySelector<HTMLButtonElement>("#inbox-new")?.addEventListener("click", () => {
    const c = el.querySelector<HTMLElement>("#inbox-compose");
    if (c) c.hidden = !c.hidden;
  });

  const form = el.querySelector<HTMLFormElement>("#inbox-compose-form");
  form?.addEventListener("submit", async (e) => {
    e.preventDefault();
    const d = new FormData(form);
    const title = String(d.get("title") ?? "").trim();
    const corpo = String(d.get("body") ?? "").trim();
    // Label é identificador, não texto de tela — mesmo slug do api.ts, senão o
    // mesmo papel gera duas labels ("Senior Dev" e "senior-dev").
    const agente = String(d.get("agent") ?? "").trim().toLowerCase().replace(/\s+/g, "-");
    if (!title || !corpo) return toast("Assunto e mensagem são obrigatórios");

    const botao = form.querySelector("button[type=submit]") as HTMLButtonElement;
    botao.disabled = true;
    try {
      const i = await gateway<GhIssue>(`github/repos/${repo}/issues`, {
        method: "POST",
        body: JSON.stringify({ title, body: corpo, labels: agente ? [`papel:${agente}`] : [] }),
      });
      toast(`Mensagem enviada como issue #${i.number}`);
      inbox(el);
    } catch (err) {
      toast((err as SourceError).message);
      botao.disabled = false;
    }
  });
}

// ------------------------------------------------------------------- thread

async function thread(el: HTMLElement, n: string): Promise<void> {
  const volta = `<a class="inbox-back" href="#/inbox">← Back to Inboxes</a>`;
  el.innerHTML = volta + head({ title: `#${n}` }) + skeleton();

  try {
    const repo = await repoAtual();
    const [issue, comentarios, eu] = await Promise.all([
      gateway<IssueFull>(`github/repos/${repo}/issues/${n}`),
      gateway<GhComment[]>(`github/repos/${repo}/issues/${n}/comments`),
      github.me(),
    ]);

    // O corpo da issue é a mensagem inicial; cada comentário, uma mensagem.
    const msgs = [
      { autor: issue.user?.login ?? "agent", quando: issue.created_at, corpo: issue.body ?? "" },
      ...comentarios.map((c) => ({ autor: c.user.login, quando: c.created_at, corpo: c.body })),
    ];

    // Bloqueado: a última mensagem da thread não é sua → o agente espera você.
    const ultima = msgs[msgs.length - 1];
    const bloqueado = ultima.autor !== eu.login;

    // Guarda a pergunta anterior já carregada — é para onde o "Back" leva.
    let anterior: string | null = null;

    const cards = msgs
      .map((m, i) => {
        const voce = m.autor === eu.login;
        const q = pergunta(m.corpo);
        const id = `inbox-q-${i}`;
        const volta = anterior;
        if (q) anterior = id;

        const sub = q ? "" : subjectDaPrimeiraLinha(m.corpo);
        const corpo = q
          ? md(q.antes) + formPergunta(q, id, volta)
          : md(sub ? m.corpo.slice(m.corpo.indexOf("\n") + 1) : m.corpo);

        return `
        <div class="card inbox-msg" data-side="${voce ? "you" : "agent"}">
          <div class="inbox-msg-head">
            <span>${voce ? "You" : esc(m.autor)}${sub ? `<span class="inbox-subj"> — ${esc(sub)}</span>` : ""}</span>
            <span class="mono">${esc(dataHora(m.quando))}</span>
          </div>
          ${corpo}
        </div>`;
      })
      .join("");

    const header = head({
      title: issue.title,
      actions: `
        <span class="mono" style="opacity:.7">Updated ${esc(since(issue.updated_at))}</span>
        <button class="btn" id="inbox-refresh">Refresh</button>`,
    });

    el.innerHTML =
      volta +
      header +
      `<div class="inbox-msgs">${cards}</div>` +
      (bloqueado
        ? `<div class="inbox-banner">The agent is waiting for your reply before continuing.</div>`
        : "") +
      `<form id="inbox-reply">
         <div class="field">
           <textarea class="textarea" name="body" rows="3"
             placeholder="${bloqueado ? "Type your answer..." : "Type your reply..."}"></textarea>
         </div>
         <div class="inbox-send">
           <button class="btn btn-primary" type="submit">Send Reply</button>
         </div>
       </form>`;

    el.querySelector<HTMLButtonElement>("#inbox-refresh")?.addEventListener("click", () => thread(el, n));

    // Form de pergunta: "Submit" é o mesmo POST do composer; "Back" só rola até
    // a pergunta anterior já carregada — não escreve nada (contrato 09).
    el.querySelectorAll<HTMLFormElement>("form.inbox-q").forEach((f) => {
      f.querySelector<HTMLButtonElement>("[data-back]")?.addEventListener("click", (ev) => {
        const alvo = (ev.currentTarget as HTMLElement).dataset.back!;
        el.querySelector(`#${alvo}`)?.scrollIntoView({ behavior: "smooth" });
      });

      f.addEventListener("submit", async (ev) => {
        ev.preventDefault();
        const escolha = String(new FormData(f).get(f.id) ?? "").trim();
        if (!escolha) return toast("Escolha uma opção");

        const botao = f.querySelector<HTMLButtonElement>("button[type=submit]")!;
        botao.disabled = true;
        try {
          await github.comment(repo, n, escolha);
          toast("Resposta enviada — o agente lê no próximo ciclo");
          thread(el, n);
        } catch (err) {
          toast((err as SourceError).message);
          botao.disabled = false;
        }
      });
    });

    const form = el.querySelector<HTMLFormElement>("#inbox-reply")!;
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const texto = String(new FormData(form).get("body") ?? "").trim();
      if (!texto) return toast("Escreva a resposta");

      const botao = form.querySelector("button")!;
      botao.disabled = true;
      try {
        await github.comment(repo, n, texto);
        toast("Resposta enviada — o agente lê no próximo ciclo");
        thread(el, n);
      } catch (err) {
        toast((err as SourceError).message);
        botao.disabled = false;
      }
    });
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML =
      volta +
      head({ title: `#${n}` }) +
      errorBox(err.source ?? "origem", err.message, err.next ?? "tente de novo");
  }
}
